import { useSelector } from "react-redux";
// @ts-expect-error: ignoring as it is existing js file
import { applicationSelector } from "@/store/slices/applicationFormSlice";
import type {
  ApplicationFormSlice,
  ApplicationState,
  Asset,
  Customer,
  CustomerDisclaimer,
  Entity,
  LoanDetails,
} from "@/store/slices/types/applicationFormSlice";
import {
  type ConditionalField,
  ConditionalSection,
} from "@/providers/LenderCardContextProvider/types/LenderProductTypes";
import {
  evaluateConditionsBySection,
  evaluateByLogicalOperators,
  getConditionalFields,
} from "./utils/conditionalFields";

const useConditionalFields = (conditionalFields?: ConditionalField[] | null) => {
  const { customers, asset, entities, loanDetails } = useSelector<ApplicationFormSlice, ApplicationState>(
    applicationSelector,
  );

  // Default return.
  if (!conditionalFields || conditionalFields.length === 0) {
    return {
      agreement: { all: [], remaining: [] },
      asset: { all: [], remaining: [] },
      customer: { all: [], remaining: [] },
      entity: { all: [], remaining: [] },
      loanDetails: { all: [], remaining: [] },
    };
  }

  const agreementFields: { all: string[]; remaining: string[] } = { all: [], remaining: [] };
  const assetFields: { all: string[]; remaining: string[] } = { all: [], remaining: [] };
  const customerFields: { all: string[]; remaining: string[] } = { all: [], remaining: [] };
  const entityFields: { all: string[]; remaining: string[] } = { all: [], remaining: [] };
  const loanDetailFields: { all: string[]; remaining: string[] } = { all: [], remaining: [] };

  conditionalFields.forEach((conditionalField) => {
    const {
      section,
      subSection,
      targetField,
      conditionType,
      conditions,
      validations,
      validationType,
      validationMessage,
      hasValidation,
    } = conditionalField;
    // Evaluate conditions by section.
    const evaluatedConditions = evaluateConditionsBySection({
      conditions,
      applicationSections: { customers, asset, entities, loanDetails },
    });

    // With the evaluated conditions, evaluate if the conditions meet the condition type (ALL or ANY).
    const meetsConditions = evaluateByLogicalOperators({
      evaluatedList: evaluatedConditions,
      logicalOperators: conditionType,
    });

    if (meetsConditions) {
      // Check for validation.
      const validationRequired = !!hasValidation && (validations ?? []).length > 0;

      switch (section) {
        case ConditionalSection.AGREEMENT: {
          // Get target sub section.
          const targetSubSection =
            subSection === "self"
              ? customers[0].customerDisclaimer
              : customers[0].customerDisclaimer[subSection as keyof CustomerDisclaimer];

          // Get conditional fields.
          const fields = getConditionalFields({
            validationRequired,
            targetSubSection,
            targetField,
            subSection,
            item: customers[0],
            validationType,
            validationMessage,
            validations: validations ?? [],
            skipIdentifier: true, // Mainly for agreement fields.
            evaluatedConditions,
          });

          agreementFields.remaining.push(...fields.remaining);
          agreementFields.all.push(...fields.all);
          break;
        }
        case ConditionalSection.ASSET: {
          // Get target sub section.
          const targetSubSection = subSection === "self" ? asset : asset[subSection as keyof Asset];

          // Get conditional fields.
          const fields = getConditionalFields({
            validationRequired,
            targetSubSection,
            targetField,
            subSection,
            item: asset,
            validationType,
            validationMessage,
            validations: validations ?? [],
            skipIdentifier: true,
            evaluatedConditions,
          });

          assetFields.remaining.push(...fields.remaining);
          assetFields.all.push(...fields.all);
          break;
        }
        case ConditionalSection.CUSTOMER: {
          // Iterate through all customers to check their target fields
          customers.forEach((customer) => {
            // Get target sub section.
            const targetSubSection = subSection === "self" ? customer : customer[subSection as keyof Customer];

            // Get conditional fields.
            const fields = getConditionalFields({
              validationRequired,
              targetSubSection: subSection === "employers" ? (targetSubSection ?? []) : targetSubSection,
              targetField,
              subSection,
              item: customer,
              validationType,
              validationMessage,
              validations: validations ?? [],
              evaluatedConditions,
            });

            customerFields.remaining.push(...fields.remaining);
            customerFields.all.push(...fields.all);
          });
          break;
        }
        case ConditionalSection.ENTITY: {
          // Iterate through all entities to check their target fields
          entities.forEach((entity) => {
            // Get target sub section.
            const targetSubSection = subSection === "self" ? entity : entity[subSection as keyof Entity];

            // Get conditional fields.
            const fields = getConditionalFields({
              validationRequired,
              targetSubSection,
              targetField,
              subSection,
              item: entity,
              validationType,
              validationMessage,
              validations: validations ?? [],
              evaluatedConditions,
            });

            entityFields.remaining.push(...fields.remaining);
            entityFields.all.push(...fields.all);
          });
          break;
        }
        case ConditionalSection.LOAN_DETAILS: {
          // Get target sub section.
          const targetSubSection = subSection === "self" ? loanDetails : loanDetails[subSection as keyof LoanDetails];

          // Get conditional fields.
          const fields = getConditionalFields({
            validationRequired,
            targetSubSection,
            targetField,
            subSection,
            item: loanDetails,
            validationType,
            validationMessage,
            validations: validations ?? [],
            skipIdentifier: true,
            evaluatedConditions,
          });

          loanDetailFields.remaining.push(...fields.remaining);
          loanDetailFields.all.push(...fields.all);
          break;
        }
      }
    }
  });

  return {
    agreement: agreementFields,
    asset: assetFields,
    customer: customerFields,
    entity: entityFields,
    loanDetails: loanDetailFields,
  };
};

export { useConditionalFields };
